import React, { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styles from '../DataExtractionTemplate.module.scss';
import { LabelError } from '../../components/editableFieldValidation';
import type { EditableField } from '../../components/editableFieldValidation';
import { SparkleWithTooltip } from 'concepts/AutoDocExtraction/Sparkle/Sparkle';
import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  IconList,
  Input,
} from 'components/core';

type Props = {
  name: string;
  index: number;
  label?: string;
  attribute?: string;
  onDelete: () => void;
  canDelete?: boolean;
  onBlur?: (event: any) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  validate: (
    value: string,
    existingFields: EditableField[]
  ) => string | undefined;
  suggestable?: (fieldName: string) => boolean;
};

const EditableField = ({
  name,
  index,
  label = 'Additional data field',
  attribute = 'name',
  onDelete,
  canDelete = true,
  validate,
  onBlur,
  disabled = false,
  fullWidth,
  suggestable = () => false,
}: Props) => {
  const { getValues, trigger, getFieldState } = useFormContext();

  useWatch({ name });
  useEffect(() => {
    return () => {
      const { error, isDirty } = getFieldState(`${name}.${index}.${attribute}`);
      if (isDirty && error) {
        trigger(`${name}.${index}.${attribute}`).then(() => '');
      }
    };
  }, [name, index, attribute]);

  return (
    <Controller
      rules={{
        required: LabelError.REQUIRED,
        onBlur,
        validate: (value: string) => {
          return validate(value, getValues(name));
        },
      }}
      name={`${name}.${index}.${attribute}` as const}
      render={({ field, fieldState }) => {
        const isSuggestable = suggestable(field.value);
        const sparkleId = `${name}.${index}.${attribute}-suggestion`;

        return (
          <div
            className={
              fullWidth ? styles.EditableFieldFullWidth : styles.EditableField
            }
          >
            <FormControl
              error={fieldState.error?.message}
              className={isSuggestable ? styles.Suggestable : undefined}
            >
              <FormLabel isVisuallyHidden>{`${label} ${index + 1}`}</FormLabel>
              <Input
                {...field}
                className={disabled ? styles.disabledEditableField : ''}
                variant={disabled ? 'outline' : 'filled'}
                data-testid={`${name}.${index}.${attribute}`}
                disabled={disabled}
                aria-describedby={isSuggestable ? sparkleId : undefined}
              />
              {isSuggestable && (
                <SparkleWithTooltip
                  id={sparkleId}
                  variant="right"
                  label={field.value}
                />
              )}
            </FormControl>
            {canDelete && (
              <Button
                data-testid={`${name}.${index}.delete`}
                iconOnly
                onClick={onDelete}
                variant="ghost"
              >
                <Icon icon={IconList.light.faTrashCan}>Delete</Icon>
              </Button>
            )}
          </div>
        );
      }}
    />
  );
};

export default EditableField;
